/* eslint-disable @typescript-eslint/no-explicit-any */

import axios from "axios";
import ENVIRONMENT from "../../environment/environment";
import { getSignalRConfig } from "../configs/SIGNALR_CONFIG";
import { SweetAlertService } from "../services/alert/sweetAlertService";
import { useAuthStore } from "../store/useAuthStore";
import { appendStaticFileAuthParams } from "./staticFileAuth";

export interface FinderUploadRequest {
    clientId: string;
    file: File;
}

export interface FinderUploadProgress {
    clientId: string;
    status: "pending" | "uploading" | "success" | "error";
    progress: number;
    error?: string;
}

export interface FinderUploadedFile {
    clientId: string;
    status: "success" | "error";
    progress: number;
    error?: string;
    file?: {
        id: string;
        name: string;
        size: number;
        mimeType: string;
        viewUrl: string;
        externalUrl?: string;
        baseUrl?: string;
    };
}

export interface FinderUploadResult {
    files: FinderUploadedFile[];
}


class FinderJs {

    private static getFileExtLower(url: string): string {
        const s = String(url || "");
        const noQuery = s.split("?")[0].split("#")[0];
        const base = noQuery.split("/").pop() || "";
        const i = base.lastIndexOf(".");
        return i >= 0 ? base.slice(i + 1).toLowerCase() : "";
    }

    private static isAuthenticatedUrl(url: string): boolean {
        try {
            const u = new URL(url);
            return u.searchParams.has("access_token") || u.searchParams.has("tenantID");
        } catch {
            return false;
        }
    }

    private static isFinderHost(url: string): boolean {
        try {
            const finderBase = (ENVIRONMENT.FILE_MANAGER || "https://finder.toppion.com.vn").replace(/\/$/, "");
            const finderHost = new URL(finderBase).hostname;
            return new URL(url).hostname === finderHost;
        } catch {
            return false;
        }
    }

    private static officeEmbedUrlIfPublic(fileUrl: string): string | null {
        const ext = FinderJs.getFileExtLower(fileUrl);
        if (!["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(ext)) return null;
        // Office Online viewer chỉ dùng được với URL public (không token, không Finder).
        if (FinderJs.isFinderHost(fileUrl) || FinderJs.isAuthenticatedUrl(fileUrl)) return null;
        return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;
    }

    /** Word/Excel/PowerPoint — trình duyệt không xem trực tiếp; iframe URL file thường báo lỗi từ Finder. */
    private static isOfficeFileUrl(url: string): boolean {
        const ext = FinderJs.getFileExtLower(url);
        return ["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(ext);
    }

    private static resolveDirectFileUrl(rawUrl: string): string | null {
        const raw = String(rawUrl || "").trim();
        if (!raw) return null;
        if (/^https?:\/\//i.test(raw)) return raw;
        if (typeof window !== "undefined" && raw.startsWith("/") && !raw.startsWith("//")) {
            return `${window.location.origin}${raw}`;
        }
        const base = String((ENVIRONMENT as any).BASE_URL || "").replace(/\/$/, "");
        if (base && raw && !raw.includes("://")) {
            return `${base}/${raw.replace(/^\//, "")}`;
        }
        return null;
    }

    private static openIframeModal(params: {
        iframeSrc: string;
        iframeTitle: string;
        onIframeReady?: (iframe: HTMLIFrameElement) => void;
    }): void {
        const { iframeSrc, iframeTitle, onIframeReady } = params;
        const modalId = `finder-preview-modal-${Date.now()}`;
        const iframeId = `finder-preview-iframe-${Date.now()}`;

        const close = () => {
            document.body.classList.remove("file-manager-modal-open");
            const escHandler = (window as any).__finderPreviewEsc;
            if (typeof escHandler === "function") {
                window.removeEventListener("keydown", escHandler);
                delete (window as any).__finderPreviewEsc;
            }
            const el = document.getElementById(modalId);
            if (el) el.remove();
        };

        const escHandler = (e: KeyboardEvent) => {
            if (e.key === "Escape") close();
        };
        (window as any).__finderPreviewEsc = escHandler;
        window.addEventListener("keydown", escHandler);

        if (!document.getElementById("finder-preview-inline-styles")) {
            const st = document.createElement("style");
            st.id = "finder-preview-inline-styles";
            st.textContent = `
                .finder-preview-overlay { position: fixed; inset: 0; z-index: 999999; display: flex; align-items: center; justify-content: center;
                  background: rgba(0,0,0,0.5); font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
                .finder-preview-box { background: #fff; border-radius: 4px; box-shadow: 0 10px 30px rgba(0,0,0,0.3); overflow: hidden;
                  width: calc(100% - 10px); height: calc(100% - 10px); max-width: 100vw; max-height: 100vh; display: flex; flex-direction: column; }
                .finder-preview-header { flex-shrink: 0; padding: 8px 12px; border-bottom: 1px solid #dee2e6; display: flex; justify-content: flex-end; align-items: center; background: #f8f9fa; }
                .finder-preview-close { background: none; border: none; font-size: 22px; cursor: pointer; color: #666; padding: 4px 8px; line-height: 1; border-radius: 4px; }
                .finder-preview-close:hover { background: #e9ecef; color: #333; }
                .finder-preview-body { flex: 1; min-height: 0; position: relative; }
                .finder-preview-iframe { border: none; width: 100%; height: 100%; display: block; }
                body.file-manager-modal-open { width: 100vw !important; height: 100vh !important; overflow: hidden !important; }
            `;
            document.head.appendChild(st);
        }

        const overlay = document.createElement("div");
        overlay.id = modalId;
        overlay.className = "finder-preview-overlay";

        const box = document.createElement("div");
        box.className = "finder-preview-box";

        const header = document.createElement("div");
        header.className = "finder-preview-header";
        const closeBtn = document.createElement("button");
        closeBtn.type = "button";
        closeBtn.className = "finder-preview-close";
        closeBtn.setAttribute("aria-label", "Đóng");
        closeBtn.innerHTML = "&times;";
        closeBtn.addEventListener("click", (e) => {
            e.stopPropagation();
            close();
        });
        header.appendChild(closeBtn);

        const body = document.createElement("div");
        body.className = "finder-preview-body";
        const iframe = document.createElement("iframe");
        iframe.id = iframeId;
        iframe.className = "finder-preview-iframe";
        iframe.title = iframeTitle;
        iframe.src = iframeSrc;
        iframe.onload = () => {
            try {
                onIframeReady?.(iframe);
            } catch {
                // ignore
            }
        };
        body.appendChild(iframe);

        box.appendChild(header);
        box.appendChild(body);
        overlay.appendChild(box);

        overlay.addEventListener("click", (e) => {
            if (e.target === overlay) close();
        });

        document.body.appendChild(overlay);
        document.body.classList.add("file-manager-modal-open");
    }

    private static openFinderAndRequestPreview(rawFileUrl: string): void {
        const tenantID = useAuthStore.getState().tenantID;
        const accessToken = useAuthStore.getState().accessToken;
        const baseUrl = (ENVIRONMENT.FILE_MANAGER || "https://finder.toppion.com.vn").replace(/\/$/, "");
        const tick = Math.random();
        const iframeSrc =
            `${baseUrl}/index?tenantID=${tenantID}&access_token=${accessToken}` +
            `&embed=true&multiple=false&maxFiles=0&allowedTypes=all&language=1&tick=${tick}` +
            `&openFileUrl=${encodeURIComponent(rawFileUrl)}&fileUrl=${encodeURIComponent(rawFileUrl)}`;

        FinderJs.openIframeModal({
            iframeSrc,
            iframeTitle: "Finder",
            onIframeReady: (iframe) => {
                // Will only work if Finder implements the listener.
                try {
                    iframe.contentWindow?.postMessage(
                        { type: "FINDER_PREVIEW_FILE", fileUrl: rawFileUrl },
                        baseUrl
                    );
                } catch {
                    // ignore
                }
            },
        });
    }

    static configPath(): any {
        const tenantID = useAuthStore.getState().tenantID;
        const accessToken = useAuthStore.getState().accessToken;
        const params = `tenantID=${tenantID}&access_token=${accessToken}&multiple=false`;
        const base = (ENVIRONMENT.FILE_MANAGER || "https://finder.toppion.com.vn").replace(/\/$/, "");
        return {
            browseUrl: `${base}/index?allowedTypes=all&${params}`,
            imageBrowseUrl: `${base}/index?allowedTypes=images&${params}`
        };
    }

    static openFileManager(options: any, callback: any): void {
        const browseFile = (window as any).browseFileManager;
        if (typeof browseFile === 'function') {
            if (typeof callback === 'function') {
                const tenantID = useAuthStore.getState().tenantID;
                const authToken = useAuthStore.getState().accessToken;
                options = { ...options, tenantID: tenantID, accessToken: authToken, authEndpoint: getSignalRConfig().authEndpoint };
                browseFile(options, callback);
            } else {
                SweetAlertService.error('Callback is not a function', 'Finder.openFileManager');
            }
        } else {
            SweetAlertService.error('browseFileManager is not available', 'Finder.openFileManager');
        }
    }

    static uploadFiles(
        files: FinderUploadRequest[],
        onProgress?: (progress: FinderUploadProgress) => void
    ): Promise<FinderUploadResult> {
        const upload = (window as any).uploadFilesToFileManager;
        const tenantID = useAuthStore.getState().tenantID;
        const accessToken = useAuthStore.getState().accessToken;

        if (!tenantID || !accessToken) {
            return Promise.reject(new Error("Finder authentication is required"));
        }

        if (typeof upload === "function") {
            return upload(files, {
                tenantID,
                accessToken,
                authEndpoint: getSignalRConfig().authEndpoint,
                onProgress,
            });
        }

        // Widget chưa deploy hàm bridge → gọi thẳng API File Manager
        return FinderJs.uploadFilesDirect(files, onProgress);
    }

    /**
     * Upload file thẳng lên thư mục "Action Plan" qua API /api/file/upload-actionplan.
     * Backend tự check/tạo thư mục và trả về link file — không cần mở File Manager.
     */
    static async uploadToActionPlan(
        files: FinderUploadRequest[],
        onProgress?: (progress: FinderUploadProgress) => void
    ): Promise<FinderUploadResult> {
        const tenantID = useAuthStore.getState().tenantID;
        const accessToken = useAuthStore.getState().accessToken;
        if (!tenantID || !accessToken) {
            throw new Error("Finder authentication is required");
        }
        try {
            return await FinderJs.uploadFilesDirect(files, onProgress);
        } catch (error) {
            // API chưa deploy / CORS chặn → fallback qua bridge widget đã deploy (nếu có)
            console.warn("[Finder] upload-actionplan failed, fallback to widget bridge", error);
            return FinderJs.uploadFiles(files, onProgress);
        }
    }

    /**
     * Gọi thẳng API File Manager (cùng-origin CORS) để upload file vào thư mục "Action Plan".
     * Ném lỗi nếu mạng/CORS (không phản hồi) để caller có thể fallback.
     */
    private static async uploadFilesDirect(
        files: FinderUploadRequest[],
        onProgress?: (progress: FinderUploadProgress) => void
    ): Promise<FinderUploadResult> {
        const { tenantID, accessToken, email } = useAuthStore.getState();
        const baseUrl = (ENVIRONMENT.FILE_MANAGER || "https://finder.toppion.com.vn").replace(/\/$/, "");
        const results: FinderUploadedFile[] = [];

        for (const item of files) {
            const clientId = item.clientId;
            onProgress?.({ clientId, status: "uploading", progress: 0 });
            try {
                const formData = new FormData();
                formData.append("File", item.file);
                const response = await axios.post(`${baseUrl}/api/file/upload-actionplan`, formData, {
                    headers: {
                        "Authorization": `Bearer ${accessToken || ""}`,
                        "X-Tenant-ID": tenantID || "",
                        "X-Email": email || "",
                    },
                    onUploadProgress: (e) => {
                        if (e.total) {
                            onProgress?.({
                                clientId,
                                status: "uploading",
                                progress: Math.round((e.loaded / e.total) * 100),
                            });
                        }
                    },
                });
                const data = response.data;
                if (data?.status) {
                    // Ưu tiên ENVIRONMENT.FILE_MANAGER — tránh Request.Host lệch / self-signed host sai khi chèn <img>.
                    const resolvedBase = (baseUrl || data.baseUrl || "").replace(/\/$/, "");
                    const viewUrl =
                        data.viewUrl ||
                        `/api/file/static/${data.fileId}/${encodeURIComponent(data.fileName)}`;
                    const file = {
                        id: data.fileId,
                        name: data.fileName,
                        size: data.fileSize,
                        mimeType: item.file.type,
                        baseUrl: resolvedBase,
                        viewUrl,
                        externalUrl: data.externalUrl,
                        fileUrl: data.fileUrl || `${resolvedBase}${viewUrl}`,
                    };
                    results.push({ clientId, status: "success", progress: 100, file });
                    onProgress?.({ clientId, status: "success", progress: 100 });
                } else {
                    const error = data?.msg || "Upload failed";
                    results.push({ clientId, status: "error", progress: 0, error });
                    onProgress?.({ clientId, status: "error", progress: 0, error });
                }
            } catch (error: any) {
                // Lỗi mạng/CORS (không có response) → báo để caller fallback
                if (!error?.response) {
                    throw error;
                }
                const msg = error?.response?.data?.msg || error?.response?.statusText || "Upload failed";
                results.push({ clientId, status: "error", progress: 0, error: msg });
                onProgress?.({ clientId, status: "error", progress: 0, error: msg });
            }
        }
        return { files: results };
    }

    /**
     * Mở Finder trong modal (iframe embed) để xem file — cùng kiểu overlay với file-manager-widget.js
     * nhưng gửi thêm query để Finder có thể nhảy tới file (openFileUrl / fileUrl).
     */
    static openFilePreview(fileUrl: string): void {
        const raw = String(fileUrl || "").trim();
        if (!raw) {
            SweetAlertService.error("Không có đường dẫn file", "Finder");
            return;
        }
        // 1) Nếu là URL file trực tiếp thì preview ngay bằng iframe file.
        const direct = appendStaticFileAuthParams(FinderJs.resolveDirectFileUrl(raw) || "");
        if (direct) {
            const officeEmbed = FinderJs.officeEmbedUrlIfPublic(direct);
            if (officeEmbed) {
                FinderJs.openIframeModal({ iframeSrc: officeEmbed, iframeTitle: "Xem tài liệu (Office)" });
                return;
            }
            // Office nhưng không public → không nhúng URL file trực tiếp (Finder báo "không thể xem trực tiếp").
            if (FinderJs.isOfficeFileUrl(direct)) {
                FinderJs.openFinderAndRequestPreview(direct);
                return;
            }
            FinderJs.openIframeModal({ iframeSrc: direct, iframeTitle: "Xem tài liệu" });
            return;
        }

        // 2) Fallback: mở Finder modal và gửi lệnh preview qua postMessage
        // (Chỉ hoạt động khi phía Finder implement listener).
        FinderJs.openFinderAndRequestPreview(raw);
    }
}

export default FinderJs;

